import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Statistic,
  Tag,
  Space,
  message,
  Alert,
  Spin,
  Avatar,
  Tabs,
  Tooltip,
  DatePicker,
  Modal,
  Select,
  Skeleton,
  Typography,
  Divider,
  Empty,
  InputNumber,
  Input,
  Form,
  Switch,
} from 'antd';
import {
  RobotOutlined,
  KeyOutlined,
  BarChartOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  WalletOutlined,
  HistoryOutlined,
  SendOutlined,
  FieldTimeOutlined,
  MoneyCollectOutlined,
  FileTextOutlined,
  DashboardOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  ApiOutlined,
  CopyOutlined,
  LinkOutlined,
  PieChartOutlined,
  LineChartOutlined,
  BarChartOutlined as BarIcon,
  SwapOutlined,
  CalendarOutlined,
  GiftOutlined,
  CrownOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import paymentService from '../../services/paymentService';
import llmService from '../../services/llmService';
import { billingService } from '../../services/billingService';

import { userPath } from '../../../shared/config/paths';
import { formatMoney, currencySymbol } from '../../../shared/utils/money';

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

// 配额转换为美元 (500000 单位 = $1)
const quotaToDollar = (quota) => {
  if (quota === undefined || quota === null) return '$0.00';
  const dollars = quota / 500000;
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(2)}K`;
  }
  if (dollars >= 0.01) {
    return `$${dollars.toFixed(2)}`;
  }
  return `$${dollars.toFixed(4)}`;
};

// 格式化数字
const formatNumber = (num) => {
  if (num === undefined || num === null || isNaN(num)) return '0';
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(2)}K`;
  }
  return num.toLocaleString();
};

// 统计卡片组件
const StatsCard = ({ group, loading }) => {
  const navigate = useNavigate();

  return (
    <Card
      size="small"
      title={
        <Space>
          {group.icon}
          <span>{group.title}</span>
        </Space>
      }
      style={{
        height: '100%',
        borderRadius: 12,
        background: group.bgColor,
      }}
      styles={{ body: { padding: '12px 16px' } }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {group.items.map((item, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: item.onClick ? 'pointer' : 'default',
            }}
            onClick={item.onClick}
          >
            <Space>
              <Avatar
                size="small"
                style={{ backgroundColor: item.avatarColor }}
                icon={item.icon}
              />
              <div>
                <div style={{ fontSize: 12, color: '#666' }}>{item.title}</div>
                {loading ? (
                  <Skeleton.Input active size="small" style={{ width: 60, height: 24 }} />
                ) : (
                  <div style={{ fontSize: 18, fontWeight: 600 }}>{item.value}</div>
                )}
              </div>
            </Space>
            {item.extra}
          </div>
        ))}
      </Space>
    </Card>
  );
};

// 图表面板组件
const ChartsPanel = ({ data, loading, modelColors }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('1');

  // 消耗趋势图配置
  const lineChartOption = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        title: { text: t('No data yet'), left: 'center', top: 'center' },
      };
    }

    const dates = [...new Set(data.map(item => {
      const date = new Date(item.created_at * 1000);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    }))];

    const quotaByDate = {};
    data.forEach(item => {
      const date = new Date(item.created_at * 1000);
      const dateStr = `${date.getMonth() + 1}/${date.getDate()}`;
      quotaByDate[dateStr] = (quotaByDate[dateStr] || 0) + (item.quota || 0);
    });

    return {
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: dates },
      yAxis: { type: 'value', axisLabel: { formatter: v => quotaToDollar(v) } },
      series: [{
        data: dates.map(d => quotaByDate[d] || 0),
        type: 'line',
        smooth: true,
        areaStyle: { opacity: 0.3 },
        lineStyle: { color: '#1890ff' },
        itemStyle: { color: '#1890ff' },
      }],
      grid: { left: '10%', right: '5%', bottom: '10%', top: '10%' },
    };
  }, [data, t]);

  // 模型消耗趋势
  const modelLineChartOption = useMemo(() => {
    if (!data || data.length === 0) {
      return { title: { text: t('No data yet'), left: 'center', top: 'center' } };
    }

    const models = [...new Set(data.map(item => item.model_name))].filter(Boolean).slice(0, 10);
    const dates = [...new Set(data.map(item => {
      const date = new Date(item.created_at * 1000);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    }))];

    const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'];

    return {
      tooltip: { trigger: 'axis' },
      legend: { data: models, bottom: 0, type: 'scroll' },
      xAxis: { type: 'category', data: dates },
      yAxis: { type: 'value', axisLabel: { formatter: v => quotaToDollar(v) } },
      series: models.map((model, idx) => {
        const modelData = data.filter(item => item.model_name === model);
        const quotaByDate = {};
        modelData.forEach(item => {
          const date = new Date(item.created_at * 1000);
          const dateStr = `${date.getMonth() + 1}/${date.getDate()}`;
          quotaByDate[dateStr] = (quotaByDate[dateStr] || 0) + (item.quota || 0);
        });
        return {
          name: model,
          type: 'line',
          smooth: true,
          data: dates.map(d => quotaByDate[d] || 0),
          lineStyle: { color: colors[idx % colors.length] },
          itemStyle: { color: colors[idx % colors.length] },
        };
      }),
      grid: { left: '10%', right: '5%', bottom: '15%', top: '10%' },
    };
  }, [data, t]);

  // 消耗分布饼图
  const pieChartOption = useMemo(() => {
    if (!data || data.length === 0) {
      return { title: { text: t('No data yet'), left: 'center', top: 'center' } };
    }

    const modelQuota = {};
    data.forEach(item => {
      if (item.model_name) {
        modelQuota[item.model_name] = (modelQuota[item.model_name] || 0) + (item.quota || 0);
      }
    });

    const pieData = Object.entries(modelQuota)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params) => `${params.name}: ${quotaToDollar(params.value)} (${params.percent}%)`
      },
      legend: { orient: 'vertical', right: 10, top: 'center', type: 'scroll' },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['40%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 14, fontWeight: 'bold' } },
        data: pieData,
      }],
    };
  }, [data, t]);

  // 调用次数排行
  const barChartOption = useMemo(() => {
    if (!data || data.length === 0) {
      return { title: { text: t('No data yet'), left: 'center', top: 'center' } };
    }

    const modelCount = {};
    data.forEach(item => {
      if (item.model_name) {
        modelCount[item.model_name] = (modelCount[item.model_name] || 0) + (item.count || 1);
      }
    });

    const sortedData = Object.entries(modelCount)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: sortedData.map(d => d.name).reverse(), axisLabel: { width: 100, overflow: 'truncate' } },
      series: [{
        type: 'bar',
        data: sortedData.map(d => d.value).reverse(),
        itemStyle: {
          color: (params) => {
            const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'];
            return colors[params.dataIndex % colors.length];
          },
          borderRadius: [0, 4, 4, 0],
        },
      }],
      grid: { left: '25%', right: '5%', bottom: '10%', top: '10%' },
    };
  }, [data, t]);

  const tabItems = [
    { key: '1', label: t('Usage breakdown'), children: <ReactECharts option={pieChartOption} style={{ height: 300 }} /> },
    { key: '2', label: t('Usage trend'), children: <ReactECharts option={modelLineChartOption} style={{ height: 300 }} /> },
    { key: '3', label: t('Quota trend'), children: <ReactECharts option={lineChartOption} style={{ height: 300 }} /> },
    { key: '4', label: t('Request rankings'), children: <ReactECharts option={barChartOption} style={{ height: 300 }} /> },
  ];

  return (
    <Card
      title={
        <Space>
          <PieChartOutlined />
          <span>{t('Model analytics')}</span>
        </Space>
      }
      style={{ borderRadius: 12 }}
      styles={{ body: { padding: '0 16px 16px' } }}
    >
      {loading ? (
        <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      )}
    </Card>
  );
};

// API 信息面板
const ApiInfoPanel = () => {
  const { t } = useTranslation();
  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        message.success(t('Copied to clipboard'));
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      message.success(t('Copied to clipboard'));
    } catch (err) {
      message.error(t('Failed to copy'));
    }
    document.body.removeChild(textarea);
  };

  const apiEndpoints = [
    { name: '/v1/chat/completions', url: 'https://llm.yeap.cloud/v1/chat/completions', desc: t('Chat API') },
    { name: '/v1/completions', url: 'https://llm.yeap.cloud/v1/completions', desc: t('Text completions endpoint') },
    { name: '/v1/embeddings', url: 'https://llm.yeap.cloud/v1/embeddings', desc: t('Embeddings API') },
    { name: '/v1/images/generations', url: 'https://llm.yeap.cloud/v1/images/generations', desc: t('Image generation API') },
    { name: '/v1/audio/transcriptions', url: 'https://llm.yeap.cloud/v1/audio/transcriptions', desc: t('Speech to text') },
    { name: '/v1/models', url: 'https://llm.yeap.cloud/v1/models', desc: t('Model list endpoint') },
  ];

  return (
    <Card
      title={
        <Space>
          <ApiOutlined />
          <span>{t('API details')}</span>
        </Space>
      }
      style={{ borderRadius: 12 }}
      styles={{ body: { padding: 0, maxHeight: 380, overflow: 'auto' } }}
    >
      {apiEndpoints.map((api, idx) => (
        <div key={idx}>
          <div style={{ padding: '12px 16px', cursor: 'pointer' }} className="api-item">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <Text strong style={{ fontSize: 13 }}>{api.name}</Text>
              <Space size="small">
                <Tooltip title={t('Copy')}>
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(api.url)}
                  />
                </Tooltip>
                <Tooltip title={t('Open')}>
                  <Button
                    type="text"
                    size="small"
                    icon={<LinkOutlined />}
                    onClick={() => window.open(api.url, '_blank')}
                  />
                </Tooltip>
              </Space>
            </div>
            <Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', color: '#1890ff', cursor: 'pointer' }}
              onClick={() => copyToClipboard(api.url)}
            >
              {api.url}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{api.desc}</Text>
          </div>
          {idx < apiEndpoints.length - 1 && <Divider style={{ margin: 0 }} />}
        </div>
      ))}
    </Card>
  );
};

const Account = () => {
  const { t } = useTranslation();
  const [account, setAccount] = useState(null);
  const [stats, setStats] = useState(null);
  const [quotaData, setQuotaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'day'),
    dayjs(),
  ]);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [transferAmount, setTransferAmount] = useState(10);
  const [transferring, setTransferring] = useState(false);
  const [userBalance, setUserBalance] = useState(0);
  const [userBalanceCurrency, setUserBalanceCurrency] = useState('USD');
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [claimingGift, setClaimingGift] = useState(null);
  const [checkinStatus, setCheckinStatus] = useState(null);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const navigate = useNavigate();
  const [transferForm] = Form.useForm();
  // LLM 充值券（划转时多送额度）+ 自动划转
  const [transferCoupons, setTransferCoupons] = useState([]);
  const [couponId, setCouponId] = useState(null);
  const [autoTransfer, setAutoTransfer] = useState(null);
  const [savingAuto, setSavingAuto] = useState(false);
  const [savedMethods, setSavedMethods] = useState(null);
  const watchedAmount = Form.useWatch('amount', transferForm) || 0;

  useEffect(() => {
    fetchAccount();
  }, []);

  useEffect(() => {
    if (account?.activated) {
      fetchStats();
      fetchCheckinStatus();
    }
  }, [account?.activated, dateRange]);

  const fetchAccount = async () => {
    setLoading(true);
    try {
      const response = await llmService.getAccount();
      if (response.success) {
        setAccount(response.data);
      } else {
        message.error(response.error || t('Failed to retrieve account information'));
      }
    } catch (error) {
      message.error(t('Failed to retrieve account information'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await llmService.getStats();
      if (response.success) {
        setStats(response.data);
        // 设置图表数据
        if (response.data?.quota_dates) {
          setQuotaData(response.data.quota_dates);
        }
      }
    } catch (error) {
      console.error('获取统计信息失败:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchCheckinStatus = async () => {
    try {
      const response = await llmService.getCheckinStatus();
      if (response.success) {
        setCheckinStatus(response.data);
      }
    } catch (error) {
      // Checkin may not be enabled, ignore
    }
  };

  const handleCheckin = async () => {
    setCheckinLoading(true);
    try {
      const response = await llmService.doCheckin();
      if (response.success) {
        message.success(response.message || t('Checked in successfully!'));
        fetchCheckinStatus();
        fetchAccount();
      } else {
        message.error(response.error || t('Check-in failed'));
      }
    } catch (error) {
      message.error(error?.error || error?.message || t('Check-in failed'));
    } finally {
      setCheckinLoading(false);
    }
  };

  const handleClaimGift = async (id) => {
    setClaimingGift(id);
    try {
      const response = await llmService.claimGift(id);
      if (response.success) {
        message.success(response.message || t('Claimed'));
        fetchAccount();
        fetchStats();
      } else {
        message.error(response.error || t('Failed to claim'));
      }
    } catch (error) {
      message.error(error?.error || error?.message || t('Failed to claim'));
    } finally {
      setClaimingGift(null);
    }
  };

  const handleRedeem = async () => {
    if (!redeemCode.trim()) {
      message.warning(t('Please enter a redemption code'));
      return;
    }
    setRedeeming(true);
    try {
      const response = await llmService.redeemCode(redeemCode.trim());
      if (response.success) {
        message.success(response.message || t('Redeemed successfully'));
        setRedeemCode('');
        fetchAccount();
      } else {
        message.error(response.error || t('Failed to redeem'));
      }
    } catch (error) {
      message.error(error?.error || error?.message || t('Failed to redeem'));
    } finally {
      setRedeeming(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      const response = await llmService.activateAccount();
      if (response.success) {
        message.success(t('LLM service activated'));
        fetchAccount();
      } else {
        message.error(response.error || t('Activation failed'));
      }
    } catch (error) {
      message.error(error.error || t('Activation failed'));
    } finally {
      setActivating(false);
    }
  };

  const handleRefresh = () => {
    fetchAccount();
    if (account?.activated) {
      fetchStats();
    }
  };

  // 打开划转弹窗
  const handleOpenTransferModal = async () => {
    try {
      const response = await billingService.getBalance();
      if (response.success && response.data) {
        setUserBalance(response.data.balance || 0);
        if (response.data.currency) setUserBalanceCurrency(response.data.currency);
      }
    } catch (error) {
      console.error('获取余额失败:', error);
    }
    transferForm.setFieldsValue({ amount: 10 });
    setCouponId(null);
    setTransferModalVisible(true);
    llmService.getTransferCoupons().then((r) => setTransferCoupons(r?.data?.items || [])).catch(() => setTransferCoupons([]));
    llmService.getAutoTransfer().then((r) => r?.success && setAutoTransfer(r.data)).catch(() => {});
    paymentService.getSavedMethods().then((r) => setSavedMethods(r?.success ? (r.data || []) : [])).catch(() => setSavedMethods([]));
  };

  const couponBonus = (c, amount) => {
    if (!c || (c.min_amount && amount < c.min_amount)) return 0;
    const b = c.type === 'fixed' ? c.value : (amount * c.value) / 100;
    return Math.round((c.max_bonus != null ? Math.min(b, c.max_bonus) : b) * 100) / 100;
  };
  const selectedCoupon = transferCoupons.find((c) => c.user_coupon_id === couponId);

  const saveAutoTransfer = async (patch) => {
    const next = { ...autoTransfer, ...patch };
    setAutoTransfer(next);
    setSavingAuto(true);
    try {
      const r = await llmService.updateAutoTransfer({ enabled: next.enabled, threshold: next.threshold, amount: next.amount,
        monthly_cap: next.monthly_cap ?? null, recharge_enabled: next.recharge_enabled, recharge_payment_method_id: next.recharge_payment_method_id });
      if (r?.success) setAutoTransfer(r.data);
      else message.error(r?.error || t('Operation failed'));
    } catch (e) {
      message.error(e?.error || t('Operation failed'));
    } finally {
      setSavingAuto(false);
    }
  };

  // 执行划转
  const handleTransfer = async () => {
    try {
      const values = await transferForm.validateFields();
      const amount = values.amount;

      if (amount > userBalance) {
        message.error(t('Transfer amount exceeds your account balance'));
        return;
      }

      setTransferring(true);
      const response = await llmService.transferToQuota(amount, couponId);
      if (response.success) {
        message.success(response.message || t('Transfer successful'));
        setTransferModalVisible(false);
        transferForm.resetFields();
        fetchAccount();
        // 刷新余额
        const balanceResponse = await billingService.getBalance();
        if (balanceResponse.success && balanceResponse.data) {
          setUserBalance(balanceResponse.data.balance || 0);
        }
      } else {
        message.error(response.error || t('Transfer failed'));
      }
    } catch (error) {
      message.error(error.error || t('Transfer failed'));
    } finally {
      setTransferring(false);
    }
  };

  // 计算性能指标
  const performanceMetrics = useMemo(() => {
    if (!quotaData || quotaData.length === 0) {
      return { avgRPM: '0', avgTPM: '0' };
    }

    const totalRequests = quotaData.reduce((sum, item) => sum + (item.count || 0), 0);
    const totalTokens = quotaData.reduce((sum, item) => sum + (item.token_used || 0), 0);

    // 计算时间跨度（分钟）
    const timeDiff = dateRange[1].diff(dateRange[0], 'minute') || 1;

    const avgRPM = (totalRequests / timeDiff).toFixed(3);
    const avgTPM = (totalTokens / timeDiff).toFixed(3);

    return { avgRPM, avgTPM };
  }, [quotaData, dateRange]);

  // 计算消耗统计
  const consumeStats = useMemo(() => {
    if (!quotaData || quotaData.length === 0) {
      return { consumeQuota: 0, consumeTokens: 0, times: 0 };
    }

    const consumeQuota = quotaData.reduce((sum, item) => sum + (item.quota || 0), 0);
    const consumeTokens = quotaData.reduce((sum, item) => sum + (item.token_used || 0), 0);
    const times = quotaData.reduce((sum, item) => sum + (item.count || 0), 0);

    return { consumeQuota, consumeTokens, times };
  }, [quotaData]);

  // 获取问候语
  const getGreeting = () => {
    const hours = new Date().getHours();
    let greeting = '';
    if (hours >= 5 && hours < 12) greeting = t('Good morning');
    else if (hours >= 12 && hours < 14) greeting = t('Good afternoon');
    else if (hours >= 14 && hours < 18) greeting = t('Good afternoon');
    else greeting = t('Good evening');
    return `${greeting}`;
  };

  // 统计卡片组数据
  const groupedStatsData = useMemo(() => {
    const userInfo = account?.user_info || {};
    const quotaInfo = account?.quota_info || {};

    return [
      {
        title: t('Account data'),
        icon: <WalletOutlined />,
        bgColor: 'linear-gradient(135deg, #e6f4ff 0%, #f0f5ff 100%)',
        items: [
          {
            title: t('Current balance'),
            value: quotaToDollar(userInfo.quota || quotaInfo.quota || 0),
            icon: <DollarOutlined />,
            avatarColor: '#1890ff',
            extra: (
              <Space size={4}>
                <Tag
                  color="green"
                  style={{ cursor: 'pointer' }}
                  onClick={handleOpenTransferModal}
                >
                  {t('Transfer')}
                </Tag>
                <Tag
                  color="blue"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(userPath('/billing/balance'))}
                >
                  {t('Top up')}
                </Tag>
              </Space>
            ),
          },
          {
            title: t('Historical usage'),
            value: quotaToDollar(userInfo.used_quota || quotaInfo.used_quota || 0),
            icon: <HistoryOutlined />,
            avatarColor: '#722ed1',
          },
        ],
      },
      {
        title: t('Usage statistics'),
        icon: <BarChartOutlined />,
        bgColor: 'linear-gradient(135deg, #f6ffed 0%, #fcffe6 100%)',
        items: [
          {
            title: t('Request count'),
            value: formatNumber(userInfo.request_count || 0),
            icon: <SendOutlined />,
            avatarColor: '#52c41a',
          },
          {
            title: t('Usage count'),
            value: formatNumber(consumeStats.times),
            icon: <FieldTimeOutlined />,
            avatarColor: '#13c2c2',
          },
        ],
      },
      {
        title: t('Resource usage'),
        icon: <ThunderboltOutlined />,
        bgColor: 'linear-gradient(135deg, #fffbe6 0%, #fff7e6 100%)',
        items: [
          {
            title: t('Quota usage'),
            value: quotaToDollar(consumeStats.consumeQuota),
            icon: <MoneyCollectOutlined />,
            avatarColor: '#faad14',
          },
          {
            title: t('Token count'),
            value: formatNumber(consumeStats.consumeTokens),
            icon: <FileTextOutlined />,
            avatarColor: '#eb2f96',
          },
        ],
      },
      {
        title: t('Performance metrics'),
        icon: <DashboardOutlined />,
        bgColor: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
        items: [
          {
            title: t('Average RPM'),
            value: performanceMetrics.avgRPM,
            icon: <ClockCircleOutlined />,
            avatarColor: '#722ed1',
          },
          {
            title: t('Average TPM'),
            value: performanceMetrics.avgTPM,
            icon: <DashboardOutlined />,
            avatarColor: '#fa8c16',
          },
        ],
      },
    ];
  }, [account, consumeStats, performanceMetrics, navigate, t]);

  if (loading && !account) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!account?.activated) {
    return (
      <Card title={<><RobotOutlined /> {t('LLM API service')}</>}>
        <Alert
          message={t('Service not activated')}
          description={t('You have not activated the LLM API service yet. Activate it to get API keys and use a variety of AI models.')}
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
        <Button
          type="primary"
          size="large"
          icon={<ThunderboltOutlined />}
          onClick={handleActivate}
          loading={activating}
        >
          {t('Activate now')}
        </Button>
      </Card>
    );
  }

  return (
    <div>
      {/* 头部 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Text style={{ fontSize: 20 }}>{getGreeting()}</Text>
        <Space>
          <RangePicker
            value={dateRange}
            onChange={(dates) => dates && setDateRange(dates)}
            allowClear={false}
            size="small"
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={statsLoading || loading}
          >
            {t('Refresh')}
          </Button>
        </Space>
      </div>

      {/* 赠送额度券（开通赠送等）：一键领取到 LLM 额度 */}
      {(account.gift_coupons || []).map((g) => (
        <Alert
          key={g.user_coupon_id}
          type="success"
          showIcon
          icon={<GiftOutlined />}
          style={{ marginBottom: 16 }}
          message={t('Gift credit: ${{amount}}', { amount: Number(g.value).toFixed(2) })}
          action={
            <Button type="primary" size="small" loading={claimingGift === g.user_coupon_id} onClick={() => handleClaimGift(g.user_coupon_id)}>
              {t('Claim')}
            </Button>
          }
        />
      ))}

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {groupedStatsData.map((group, idx) => (
          <Col xs={24} sm={12} lg={6} key={idx}>
            <StatsCard group={group} loading={statsLoading} />
          </Col>
        ))}
      </Row>

      {/* 图表和 API 信息 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={18}>
          <ChartsPanel data={quotaData} loading={statsLoading} />
        </Col>
        <Col xs={24} lg={6}>
          <ApiInfoPanel />
        </Col>
      </Row>

      {/* 签到 & 兑换 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {checkinStatus && !checkinStatus.disabled && (
          <Col xs={24} sm={12} md={8}>
            <Card
              style={{
                borderRadius: 12,
                background: checkinStatus?.is_checked_in
                  ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
                  : 'linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Space>
                  <Avatar
                    style={{ backgroundColor: checkinStatus?.is_checked_in ? '#52c41a' : '#1890ff' }}
                    icon={<CalendarOutlined />}
                  />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('Daily check-in')}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('{{days}} consecutive days', { days: checkinStatus?.continuous_checkin_count || checkinStatus?.streak || 0 })}
                    </Text>
                  </div>
                </Space>
                <Button
                  type="primary"
                  icon={checkinStatus?.is_checked_in ? <CheckCircleOutlined /> : <GiftOutlined />}
                  onClick={handleCheckin}
                  loading={checkinLoading}
                  disabled={checkinStatus?.is_checked_in}
                  size="small"
                >
                  {checkinStatus?.is_checked_in ? t('Checked in') : t('Check in')}
                </Button>
              </div>
            </Card>
          </Col>
        )}
        <Col xs={24} sm={12} md={checkinStatus && !checkinStatus.disabled ? 16 : 24}>
          <Card style={{ borderRadius: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar style={{ backgroundColor: '#fa8c16' }} icon={<GiftOutlined />} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('Top up with redemption code')}</div>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder={t('Please enter a redemption code')}
                    value={redeemCode}
                    onChange={(e) => setRedeemCode(e.target.value)}
                    onPressEnter={handleRedeem}
                  />
                  <Button type="primary" onClick={handleRedeem} loading={redeeming}>
                    {t('Redeem')}
                  </Button>
                </Space.Compact>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 快捷入口 */}
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={6}>
          <Card
            hoverable
            onClick={() => navigate(userPath('/llm/tokens'))}
            style={{ cursor: 'pointer', borderRadius: 12 }}
          >
            <Statistic
              title={t('API key')}
              value={t('Manage keys')}
              prefix={<KeyOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            hoverable
            onClick={() => navigate(userPath('/llm/logs'))}
            style={{ cursor: 'pointer', borderRadius: 12 }}
          >
            <Statistic
              title={t('Usage logs')}
              value={t('View logs')}
              prefix={<BarChartOutlined style={{ fontSize: 24, color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            hoverable
            onClick={() => navigate(userPath('/llm/models'))}
            style={{ cursor: 'pointer', borderRadius: 12 }}
          >
            <Statistic
              title={t('Model pricing')}
              value={t('View models')}
              prefix={<DollarOutlined style={{ fontSize: 24, color: '#faad14' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            hoverable
            onClick={() => navigate(userPath('/llm/subscription'))}
            style={{ cursor: 'pointer', borderRadius: 12 }}
          >
            <Statistic
              title={t('Subscriptions')}
              value={t('View subscriptions')}
              prefix={<CrownOutlined style={{ fontSize: 24, color: '#722ed1' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* 划转弹窗 */}
      <Modal
        title={
          <Space>
            <SwapOutlined />
            <span>{t('Transfer balance to LLM API')}</span>
          </Space>
        }
        open={transferModalVisible}
        onCancel={() => setTransferModalVisible(false)}
        onOk={handleTransfer}
        okText={t('Confirm transfer')}
        confirmLoading={transferring}
        destroyOnClose
      >
        <Alert
          message={t('Transfer details')}
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>{t('Transfer main account balance to LLM API quota')}</li>
              <li>{t('Transfers cannot be reversed. Please confirm the amount.')}</li>
              <li>{t('{{amount}} = {{quota}} quota', { amount: formatMoney(1, userBalanceCurrency, { original: true }), quota: '$1.00' })}</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#f5f5f5', borderRadius: 8 }}>
          <Text type="secondary">{t('Available main account balance:')}</Text>
          <Text strong style={{ fontSize: 18, marginLeft: 8 }}>{formatMoney(userBalance, userBalanceCurrency, { original: true })}</Text>
        </div>

        <Form form={transferForm} layout="vertical">
          <Form.Item
            name="amount"
            label={t('Transfer amount')}
            rules={[
              { required: true, message: t('Please enter a transfer amount') },
              { type: 'number', min: 1, message: t('Minimum transfer amount is {{amount}}', { amount: formatMoney(1, userBalanceCurrency, { original: true }) }) },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={userBalance}
              precision={2}
              prefix={currencySymbol(userBalanceCurrency)}
              placeholder={t('Please enter a transfer amount')}
              addonAfter={
                <Space>
                  <Button type="link" size="small" onClick={() => transferForm.setFieldsValue({ amount: 10 })}>10</Button>
                  <Button type="link" size="small" onClick={() => transferForm.setFieldsValue({ amount: 50 })}>50</Button>
                  <Button type="link" size="small" onClick={() => transferForm.setFieldsValue({ amount: 100 })}>100</Button>
                  <Button type="link" size="small" onClick={() => transferForm.setFieldsValue({ amount: userBalance })}>{t('All')}</Button>
                </Space>
              }
            />
          </Form.Item>
        </Form>

        {transferCoupons.length > 0 && (
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder={t('Use a top-up coupon')}
            value={couponId}
            onChange={(v) => setCouponId(v ?? null)}
            options={transferCoupons.map((c) => ({
              value: c.user_coupon_id,
              disabled: !!(c.min_amount && watchedAmount < c.min_amount),
              label: `${c.name} · ${c.type === 'fixed' ? t('+{{v}} quota', { v: `$${c.value}` }) : t('+{{v}}% quota', { v: c.value })}${c.min_amount ? ` · ${t('min {{v}}', { v: `$${c.min_amount}` })}` : ''}`,
            }))}
          />
        )}

        <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
          <Text type="secondary">
            {t('Quota after transfer: approximately {{quota}}', { quota: quotaToDollar((watchedAmount + couponBonus(selectedCoupon, watchedAmount)) * 500000) })}
          </Text>
        </div>

        {autoTransfer && (
          <>
            <Divider style={{ margin: '16px 0 12px' }} />
            <Space align="center" wrap>
              <Switch size="small" checked={autoTransfer.enabled} loading={savingAuto} onChange={(v) => saveAutoTransfer({ enabled: v })} />
              <Text>{t('Auto-transfer')}</Text>
              {autoTransfer.enabled && (
                <>
                  <Text type="secondary">{t('when quota is below')}</Text>
                  <InputNumber size="small" min={0} max={10000} precision={2} prefix="$" style={{ width: 100 }}
                    value={autoTransfer.threshold} onChange={(v) => setAutoTransfer({ ...autoTransfer, threshold: v ?? 0 })}
                    onBlur={() => saveAutoTransfer({})} />
                  <Text type="secondary">{t('transfer')}</Text>
                  <InputNumber size="small" min={1} max={10000} precision={2} prefix="$" style={{ width: 100 }}
                    value={autoTransfer.amount} onChange={(v) => setAutoTransfer({ ...autoTransfer, amount: v ?? 1 })}
                    onBlur={() => saveAutoTransfer({})} />
                  <Tooltip title={t('Used this month: ${{amount}}', { amount: Number(autoTransfer.month_used || 0).toFixed(2) })}>
                    <Text type="secondary">{t('Monthly limit')}</Text>
                  </Tooltip>
                  <InputNumber size="small" min={1} max={100000} precision={2} prefix="$" style={{ width: 110 }} placeholder={t('No limit')}
                    value={autoTransfer.monthly_cap} onChange={(v) => setAutoTransfer({ ...autoTransfer, monthly_cap: v })}
                    onBlur={() => saveAutoTransfer({})} />
                </>
              )}
            </Space>
            {autoTransfer.enabled && (
              <div style={{ marginTop: 8 }}>
                <Space align="center" wrap>
                  <Switch size="small" checked={autoTransfer.recharge_enabled} loading={savingAuto}
                    disabled={!autoTransfer.recharge_enabled && savedMethods !== null && savedMethods.length === 0}
                    onChange={(v) => saveAutoTransfer({ recharge_enabled: v })} />
                  <Text type="secondary">{t('If the balance is short, top it up with')}</Text>
                  {savedMethods && savedMethods.length > 0 ? (
                    <Select size="small" style={{ minWidth: 200 }}
                      value={autoTransfer.recharge_payment_method_id ?? 0}
                      onChange={(v) => saveAutoTransfer({ recharge_payment_method_id: v || null })}
                      options={[{ value: 0, label: t('Default payment method') },
                        ...savedMethods.map((m) => ({ value: m.id, label: m.label || `${m.card_brand || m.type} •••• ${m.card_last4 || m.last4 || ''}` }))]} />
                  ) : (
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(`${userPath('/billing/balance')}?tab=payment-methods`)}>
                      {t('Add a payment method')}
                    </Button>
                  )}
                </Space>
              </div>
            )}
            {autoTransfer.enabled && autoTransfer.last_error && (
              <div><Text type="warning" style={{ fontSize: 12 }}>{t('Last auto-transfer failed: {{error}}', { error: autoTransfer.last_error })}</Text></div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default Account;
